import {useNavigate} from 'react-router-dom'
import {useMutation} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useI18n} from '@/i18n'
import {routes} from '@/lib/routes'
import {useServices} from '@/services/context'
import {ApiError} from '@/api/client'
import {IamErrorCode} from '@/api/error-codes'
import {useToast} from '@/hooks/use-toast'
import type {RuleRequestReviewRequest, RuleTest} from '@/types/auto-assign-rule-request'
import type {RuleTestRequest} from '@/types/auto-assign-rule-request'

export interface UseAutoAssignRuleRequestMutationsOptions {
    onSubmitSuccess?: () => void
    onApproveSuccess?: () => void
    onRejectSuccess?: () => void
    onTestSuccess?: (data: RuleTest) => void
    onTestError?: (err: Error) => void
}

/**
 * Composite mutation hook for the AutoAssignRuleRequestDetail page (and the
 * wizard in 603.08).
 *
 * Members:
 *  - submit         — submit DRAFT for review (no payload)
 *  - approve        — approve SUBMITTED with a comment
 *  - reject         — reject SUBMITTED with a comment
 *  - runTest        — execute the rule test (paramsless from the page)
 *  - deleteRequest  — delete the request and navigate back to the list
 */
export function useAutoAssignRuleRequestMutations(
    id: string | undefined,
    options: UseAutoAssignRuleRequestMutationsOptions = {},
) {
    const services = useServices()
    const navigate = useNavigate()
    const i18n = useI18n()
    const {toast} = useToast()
    const {onSubmitSuccess, onApproveSuccess, onRejectSuccess, onTestSuccess, onTestError} = options

    /* eslint-disable i18next/no-literal-string -- TanStack Query keys, not user-visible strings */
    const invalidateBoth = [['rule-request', id], ['rule-requests']] as const
    const invalidateListOnly = ['rule-requests'] as const
    /* eslint-enable i18next/no-literal-string */

    const submit = useEntityMutation({
        mutationFn: () => services.autoAssignRules.submitRequest(id!),
        invalidate: [...invalidateBoth],
        successMessage: i18n.autoAssignRuleRequests.requestSubmitted,
        onSuccess: () => onSubmitSuccess?.(),
        onError: (err: Error) => {
            if (err instanceof ApiError && err.code === IamErrorCode.GV0032_RULE_NO_OWNER) {
                toast({description: i18n.autoAssignRules.ruleNoOwner, variant: 'destructive'})
            }
        },
    })

    const approve = useEntityMutation({
        mutationFn: (data: RuleRequestReviewRequest) =>
            services.autoAssignRules.approveRequest(id!, data),
        invalidate: [...invalidateBoth],
        successMessage: i18n.autoAssignRuleRequests.requestApproved,
        onSuccess: () => onApproveSuccess?.(),
    })

    const reject = useEntityMutation({
        mutationFn: (data: RuleRequestReviewRequest) =>
            services.autoAssignRules.rejectRequest(id!, data),
        invalidate: [...invalidateBoth],
        successMessage: i18n.autoAssignRuleRequests.requestRejected,
        onSuccess: () => onRejectSuccess?.(),
    })

    const runTest = useMutation({
        mutationFn: (data: RuleTestRequest) =>
            services.autoAssignRules.testExistingRequest(id!, data),
        onSuccess: (data) => onTestSuccess?.(data),
        onError: (err: Error) => onTestError?.(err),
    })

    const deleteRequest = useEntityMutation({
        mutationFn: () => services.autoAssignRules.removeRequest(id!),
        invalidate: [...invalidateListOnly],
        successMessage: i18n.autoAssignRuleRequests.requestDeleted,
        onSuccess: () => {
            navigate(routes.accessGovernance.autoAssignRuleRequests.list())
        },
    })

    return {submit, approve, reject, runTest, deleteRequest}
}
